import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IRenderAction, IRouteValue, MethodType, RouteValueBindingSource } from '../../models';
import { DataProvider } from '../../providers/basic/data.service';
import { MethodsService } from '../methods/methods.service';

export type QueryParamDataType = string | number | boolean | undefined | null;

@Injectable({
  providedIn: 'root'
})
export class ActionsService extends DataProvider<any> {
  // Services
  private readonly methodsService: MethodsService = inject(MethodsService);

  // Methods
  /**
   * Calls the specified method
   * @param action to call
   * @param routeValues to use for the call
   * @returns the result of the action
   */
  callAction<T>(action: IRenderAction, queryParamData?: Record<string, QueryParamDataType>): Observable<T | undefined> {
    const methodUrl = this.buildUrlWithQueryParameters(action.url, action.routeValues, queryParamData).split('/');
    methodUrl.splice(0, 2); // Remove first empty element and "api" part
    const url = this.url + methodUrl.join('/');
    return this.methodsService.generateRequest<T>(MethodType.GET, url);
  }

  /**
   * Constructs a url from the given url and route values
   * @param url to build the full url from
   * @param routeValues to use for the url
   * @param data to use for the route values
   * @returns the full url with the route values
   */
  private buildUrlWithQueryParameters(
    url: string,
    routeValues: IRouteValue[] | { ignoreRequest: boolean },
    data?: Record<string, QueryParamDataType>
  ): string {
    let urlWithParameters = url;
    if ('ignoreRequest' in routeValues) routeValues = [];
    const queryRouteValues: IRouteValue[] = routeValues.filter((rv) => rv.bindingSource === RouteValueBindingSource.QUERY);
    if (queryRouteValues.length === 0) return urlWithParameters;
    const providedDataRouteValues = queryRouteValues.filter((rv) => {
      return rv.defaultValue != null || data?.[rv.parameterName] != null;
    });
    providedDataRouteValues.forEach((rv) => {
      urlWithParameters += urlWithParameters.includes('?') ? '&' : '?';
      // Values must be encoded: base64 search terms contain '+', '/' and '=', and an unencoded '+'
      // arrives as a space on the backend, which makes the search term undecodable.
      urlWithParameters += `${rv.parameterName}=${encodeURIComponent(String(data?.[rv.parameterName] ?? rv.defaultValue))}`;
    });
    return urlWithParameters;
  }
}
